import React, { useEffect, useState, useRef, memo } from 'react';
type SplashScreenProps = {
  /** Optional override; defaults to the uploaded Sora visual */
  imageUrl?: string;
  /** ms before auto-dismiss; default 6000 */
  autoHideMs?: number;
  /** called when intro finishes or is dismissed */
  onDone?: () => void;
};
const LS_KEY = 'sinshift_splash_last_seen';
const NeonBackdrop: React.FC = memo(() => {
  // pure CSS glow, no canvas or undefined "size" refs
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
    background: 'radial-gradient(1200px 600px at 60% -10%, rgba(244,0,255,0.22), transparent 60%), radial-gradient(800px 500px at 20% 110%, rgba(255,46,138,0.18), transparent 60%)',
    filter: 'saturate(120%) blur(0px)'
  }} data-id="element-20564" />;
});
const SplashScreen: React.FC<SplashScreenProps> = ({
  imageUrl = 'https://uploadthingy.s3.us-west-1.amazonaws.com/ctUKNy7zNs9njQaTKAMctq/welcome_to_sinshift_astonmartin_b.webp',
  autoHideMs = 6000,
  onDone
}) => {
  const [visible, setVisible] = useState(true);
  const [closing, setClosing] = useState(false);
  const timerRef = useRef<number | null>(null);
  // Safety: guard against missing global types in some TS toolchains
  const now = (() => {
    try {
      return Date.now();
    } catch {
      return new Date().getTime();
    }
  })();
  useEffect(() => {
    // Auto dismiss
    timerRef.current = window.setTimeout(() => startClose(), autoHideMs);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') startClose();
    };
    window.addEventListener('keydown', onKey);
    // Cleanup
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      window.removeEventListener('keydown', onKey);
    };
  }, [autoHideMs]);
  const startClose = () => {
    if (closing) return;
    setClosing(true);
    // give time for fade-out animation
    window.setTimeout(() => {
      setVisible(false);
      if (onDone) onDone();
    }, 450);
  };
  if (!visible) return null;
  return <div role="dialog" aria-label="Welcome to SinShift" aria-modal="true" className={`fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(3,6,20,0.65)] backdrop-blur-sm transition-opacity duration-300 ${closing ? 'opacity-0' : 'opacity-100'}`} onClick={startClose} data-id="element-20565">
      <NeonBackdrop data-id="element-20566" />
      <div className="relative mx-4 w-full max-w-5xl" onClick={e => e.stopPropagation()} // allow clicking buttons/links later
    data-id="element-20567">
        {/* Card */}
        <div className={`relative overflow-hidden rounded-3xl border border-white/10 bg-[#0f1222]/80 shadow-2xl transition-all duration-300 ${closing ? 'scale-[0.98]' : 'scale-100'}`} style={{
        // glass vignette
        boxShadow: '0 30px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.05) inset'
      }} data-id="element-20568">
          {/* Subtle top neon beam */}
          <div className="pointer-events-none absolute -top-16 left-1/2 h-40 w-[120%] -translate-x-1/2 rounded-full opacity-80 blur-2xl" style={{
          background: 'linear-gradient(90deg, rgba(255,46,138,0.35), rgba(138,43,226,0.35))'
        }} data-id="element-20569" />
          {/* Content row */}
          <div className="grid grid-cols-1 items-center gap-0 md:grid-cols-12" data-id="element-20570">
            {/* Image column (not full screen) */}
            <div className="md:col-span-7" data-id="element-20571">
              <img src={imageUrl} alt="Welcome to SinShift" className="h-[54vh] w-full object-cover md:rounded-l-3xl" onError={e => {
              // fallback to neutral gradient if image not found
              ;
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }} data-id="element-20572" />
            </div>
            {/* Copy column */}
            <div className="relative md:col-span-5 p-6 md:p-8" data-id="element-20573">
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white" data-id="element-20574">
                Welcome to{' '}
                <span className="bg-gradient-to-r from-pink-500 via-fuchsia-500 to-violet-500 bg-clip-text text-transparent" data-id="element-20575">
                  SinShift
                </span>
              </h1>
              <p className="mt-3 text-sm md:text-base text-white/70" data-id="element-20576">
                Your daily rituals, rewards and connections—wrapped in neon
                magic. Click anywhere or press{' '}
                <kbd className="rounded bg-white/10 px-1" data-id="element-20577">Esc</kbd> to continue.
              </p>
              {/* Soft CTA row */}
              <div className="mt-6 flex items-center gap-3" data-id="element-20578">
                <button onClick={startClose} className="rounded-full bg-gradient-to-r from-pink-500 to-violet-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-pink-500/20 hover:shadow-violet-500/30 focus:outline-none focus:ring-2 focus:ring-pink-400" data-id="element-20579">
                  Enter SinShift
                </button>
                <button onClick={startClose} className="rounded-full border border-white/15 px-5 py-2 text-sm font-medium text-white/80 hover:bg-white/5" data-id="element-20580">
                  Skip
                </button>
              </div>
              {/* Bottom aura */}
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-12 left-1/2 h-28 w-[140%] -translate-x-1/2 blur-2xl opacity-70" style={{
              background: 'radial-gradient(60% 90% at 50% 50%, rgba(255,46,138,0.35), transparent 60%), radial-gradient(40% 80% at 60% 60%, rgba(138,43,226,0.32), transparent 60%)'
            }} data-id="element-20581" />
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default SplashScreen;